/**
 * The module store behind `GuidePageContext` — decision G13.
 *
 * Module state, like the adapter's `activeTarget`, for the same reason: the header and the
 * page share no provider, and adding one to the app shell for a help feature would be the
 * help feature reaching into the shell. There is only ever one page mounted, so one slot.
 */

import { useSyncExternalStore } from 'react';

export interface GuidePageContextValue {
  /** The tool this page belongs to, e.g. `bpm`. Overrides route matching. */
  toolKey: string;
  /** The location on that tool, e.g. `bpm.add_guest.page`. */
  locationKey?: string;
  /**
   * Host identifiers the tool already holds — for BPM, the sticky occurrence. Integer
   * `*_id` values only; the server ignores anything else.
   */
  contextIds?: Record<string, number>;
  /** Whether the tool currently holds unsaved input, for the exit warning. */
  hasUnsavedToolData?: boolean;
}

let current: GuidePageContextValue | null = null;
const listeners = new Set<() => void>();

export function publish(value: GuidePageContextValue | null) {
  current = value;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function snapshot() {
  return current;
}

/** The context the mounted page has published, or null. Read by `GuideButton`. */
export function usePublishedGuidePageContext(): GuidePageContextValue | null {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
